import { FocusEvent, memo, ReactNode, Ref, SyntheticEvent, useCallback } from 'react'
import cn from 'classnames'
import { twm } from 'helpers'
import noop from 'lodash/noop'

import s from './Button.module.less'

export type TGroupPosition = 'start' | 'center' | 'end'

export interface IButtonProps {
  additional?: ReactNode
  appearance?: 'primary' | 'secondary' | 'info' | 'success' | 'transparent' | 'danger'
  block?: boolean
  children?: ReactNode
  className?: string
  contentContainerClassName?: string
  contentPosition?: 'start' | 'center' | 'end'

  disabled?: boolean
  groupPositionH?: TGroupPosition

  groupPositionV?: TGroupPosition
  id?: string
  innerRef?: Ref<HTMLButtonElement>
  loading?: boolean
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void
  onClick?: (event: SyntheticEvent<HTMLButtonElement>) => void
  onFocus?: (event: FocusEvent<HTMLButtonElement>) => void
  onMouseEnter?: (event: SyntheticEvent<HTMLButtonElement>) => void
  onMouseLeave?: (event: SyntheticEvent<HTMLButtonElement>) => void
  postfix?: JSX.Element
  prefix?: JSX.Element
  size?: 'xs' | 'sm' | 'nm' | 'lg' | 'xl'
  tabIndex?: number
  testId?: string
  title?: string
  type?: 'button' | 'submit'
  wide?: boolean
}

export const ButtonNoMemo = ({
  id,
  contentContainerClassName,
  type = 'button',
  loading = false,
  disabled = false,
  additional,
  innerRef,
  onClick = noop,
  onMouseLeave,
  onMouseEnter,
  onBlur,
  onFocus,
  children,
  className,
  size = 'nm',
  appearance = 'secondary',
  groupPositionH,
  groupPositionV,
  contentPosition = 'center',
  wide,
  block,
  prefix,
  postfix,
  title,
  tabIndex,
  testId
}: IButtonProps) => {
  const handleClick = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      if (!disabled && !loading) {
        onClick(event)
      }
    },
    [disabled, loading, onClick]
  )

  const solid = ['primary', 'success', 'danger'].includes(appearance)
  const containerClassName = twm(
    cn(
      'inline-flex select-none relative align-middle items-center rounded-[3px] border-none p-0 text-center cursor-pointer whitespace-nowrap font-normal [transition:box-shadow_200ms_ease-in-out,background-color_200ms_ease-in-out,color_200ms_ease-in-out] disabled:cursor-default disabled:bg-[#ebe9ee] disabled:shadow-none',
      s.Button,
      'disabled:text-[rgba(0,0,0,0.54)]',
      {
        flex: block,
        'w-full': wide,
        'bg-[#ff5e74] hover:bg-[#ff4f67] active:bg-[#ff3f5a] focus:shadow-[0_0_0_2px_rgba(255,94,116,0.4)]':
          appearance === 'danger',
        'bg-[#fff] shadow-[inset_0_0_0_1px_#0087ff] hover:bg-[#f0f3f7] active:bg-[#ebf1ff] focus:shadow-[inset_0_0_0_1px_#0087ff,0_0_0_2px_rgba(0,135,255,0.999)]':
          appearance === 'info',
        'text-[#0087ff]': appearance === 'info',
        'bg-[#0087ff] hover:bg-[#007ff0] active:bg-[#0077e0] focus:shadow-[0_0_0_2px_rgba(0,135,255,0.4)] focus:z-1':
          appearance === 'primary',
        'bg-[#fff] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.2)] hover:bg-[#f0f3f7] active:bg-[#ebf1ff] focus:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.2),0_0_0_2px_rgba(0,0,0,0.199)] [&_svg]:text-[rgba(0,0,0,0.72)]':
          appearance === 'secondary',
        '[&_svg]:text-[rgba(0,0,0,0.38)]':
          appearance === 'secondary' && !children && Boolean(postfix) !== Boolean(prefix),
        'bg-[rgba(255,255,255,0.12)] hover:bg-[rgba(255,255,255,0.16)] focus:bg-[#fff] focus:shadow-[0_0_0_2px_#fff] active:bg-[rgba(255,255,255,0.22)]':
          appearance === 'transparent',
        'text-[#fff]': appearance === 'transparent' || solid,
        'bg-[#24b377] hover:bg-[##21a66f] active:bg-[#1f9a66] focus:shadow-[0_0_0_2px_rgba(36,179,119,0.4)] focus:z-1':
          appearance === 'success',
        'min-h-[24px] min-w-[24px] py-0 px-1 [&_.text]:my-0 [&_.text]:mx-1': size === 'xs',
        'text-body-md min-h-[28px] min-w-[28px] py-0 px-1 [&_.text]:my-0 [&_.text]:mx-1': size === 'sm',
        'text-body-md min-h-[32px] min-w-[32px] py-0 px-2 [&_.text]:my-0 [&_.text]:mx-1': size === 'nm',
        'text-body-md min-h-[40px] min-w-[40px] py-0 px-4 [&_.text]:my-0 [&_.text]:mx-2': size === 'lg',
        'text-body-md min-h-[48px] min-w-[40px] py-0 px-5 [&_.text]:my-0 [&_.text]:mx-2': size === 'xl',
        'justify-start': contentPosition === 'start',
        'justify-center': contentPosition === 'center',
        'justify-end': contentPosition === 'end',
        'rounded-tr-none rounded-br-none': groupPositionH === 'start',
        'rounded-none mt-[-1px]': groupPositionH === 'center' || groupPositionV === 'center',
        'rounded-tl-none rounded-bl-none mt-[-1px]': groupPositionH === 'end',
        'rounded-bl-none rounded-br-none': groupPositionV === 'start',
        'rounded-tl-none rounded-tr-none mt-[-1px]': groupPositionV === 'end',
        'ml-[-4px]': prefix && (size === 'xs' || size === 'nm'),
        'ml-[-2px]': prefix && size === 'sm',
        'flex-none': postfix,
        'mr-[-4px]': postfix && (size === 'xs' || size === 'nm'),
        'mr-[-2px]': postfix && size === 'sm',
        'm-0': (postfix || prefix) && !children && Boolean(postfix) !== Boolean(prefix),
        [s.Button_loading]: loading,
        'cursor-default bg-[#ebe9ee] text-[rgba(0,0,0,0.54)]': loading,
        'p-0': !children && Boolean(postfix) !== Boolean(prefix)
      }
    ),
    className
  )

  return (
    <button
      ref={innerRef}
      className={containerClassName}
      data-test-id={testId}
      disabled={disabled}
      id={id}
      onBlur={onBlur}
      onClick={handleClick}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      tabIndex={tabIndex}
      title={title}
      // eslint-disable-next-line react/button-has-type
      type={type}
    >
      {!!prefix && <prefix.type {...prefix.props} />}
      {!!children && (
        <span className={cn('text overflow-hidden text-ellipsis', contentContainerClassName)}>{children}</span>
      )}
      {!!postfix && <postfix.type className="flex-none" {...postfix.props} />}
      {additional}
    </button>
  )
}

export const Button = memo(ButtonNoMemo)
